import React, { useState, useEffect } from 'react';
import { Wrench, DollarSign, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const InstallationPricingTab = ({ token }) => {
  const [pricing, setPricing] = useState({
    base_price: 75,
    price_per_sqft: 2.50,
    motorized_addon: 25,
    high_ceiling_addon: 50,
    minimum_charge: 150
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/installation-pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch installation pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePricing = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/installation-pricing`, pricing, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Installation pricing updated');
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setPricing(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Calculate example price
  const examplePrice = () => {
    const sqft = (48 * 60) / 144; // 48" x 60" window
    let price = pricing.base_price + (sqft * pricing.price_per_sqft);
    return Math.max(price, pricing.minimum_charge);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">Installation Pricing</h3>
          <p className="text-sm text-neutral-500">
            Set labor prices for professional installation services
          </p>
        </div>
        <Button onClick={savePricing} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Pricing Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Wrench className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">How Installation Pricing Works</p>
            <p className="text-sm text-amber-700 mt-1">
              Installation cost = Base Price + (Square Footage × Price per Sq Ft) + Add-ons. 
              The final price will never be less than the Minimum Charge.
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Fields */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-neutral-700">Base Price (per window)</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pricing.base_price}
                onChange={(e) => updateField('base_price', e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">Starting price for each window installation</p>
          </div>

          <div>
            <Label className="text-sm font-medium text-neutral-700">Price per Square Foot</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pricing.price_per_sqft}
                onChange={(e) => updateField('price_per_sqft', e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">Additional cost based on shade size</p>
          </div>

          <div>
            <Label className="text-sm font-medium text-neutral-700">Minimum Charge</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pricing.minimum_charge}
                onChange={(e) => updateField('minimum_charge', e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">Minimum installation fee regardless of size</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-neutral-700">Motorized Add-on</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pricing.motorized_addon}
                onChange={(e) => updateField('motorized_addon', e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">Extra charge for motorized shade installation</p>
          </div>

          <div>
            <Label className="text-sm font-medium text-neutral-700">High Ceiling Add-on</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pricing.high_ceiling_addon}
                onChange={(e) => updateField('high_ceiling_addon', e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">Extra for ceilings over 10 feet</p>
          </div>

          {/* Example Calculation */}
          <div className="bg-neutral-100 rounded-lg p-4 mt-4">
            <p className="text-sm font-medium text-neutral-700 mb-2">Example Calculation</p>
            <p className="text-xs text-neutral-600">48" × 60" standard window:</p>
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600">Base price:</span>
                <span className="font-mono">${pricing.base_price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Area ({((48*60)/144).toFixed(1)} sq ft × ${pricing.price_per_sqft}):</span>
                <span className="font-mono">${(((48*60)/144) * pricing.price_per_sqft).toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-neutral-200 font-medium">
                <span>Installation total:</span>
                <span className="font-mono text-amber-600">${examplePrice().toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallationPricingTab;
